/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * Confidential and proprietary. Author: Alexander Orlov.
 *
 * Tenant-admin storefront settings: branding, subdomain reservation, and bring-your-own
 * custom-domain provisioning (DNS + App Service binding + SSL via the backend / Azure).
 */

import { useEffect, useState } from 'react';
import { Globe, Check, Loader2, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Tenant, DomainStatus } from '../types';
import api from '../services/api';

export default function SiteSettingsPage() {
  const { t } = useTranslation();
  const [site, setSite] = useState<Partial<Tenant>>({});
  const [subdomainStatus, setSubdomainStatus] = useState<string>();
  const [customStatus, setCustomStatus] = useState<string>();
  const [domainLive, setDomainLive] = useState<DomainStatus | null>(null);
  const [subInput, setSubInput] = useState('');
  const [domainInput, setDomainInput] = useState('');
  const [msg, setMsg] = useState<string>('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const s = await api.getOwnerSite();
      setSite(s.result || {});
      setSubdomainStatus(s.subdomainStatus);
      setCustomStatus(s.customDomainStatus);
      setSubInput(s.result?.subdomain || '');
      setDomainInput(s.result?.customDomain || '');
    } catch {
      setMsg(t('settings.signInHint'));
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Poll the live custom-domain DNS/SSL status while it is provisioning.
  useEffect(() => {
    if (!site.customDomain) return;
    let stop = false;
    const tick = async () => {
      try {
        const r = await api.customDomainStatus();
        if (!stop && r.configured) setDomainLive(r.status || null);
      } catch {
        /* ignore */
      }
    };
    tick();
    const id = setInterval(tick, 8000);
    return () => {
      stop = true;
      clearInterval(id);
    };
  }, [site.customDomain]);

  const saveBranding = async () => {
    setBusy(true);
    setMsg('');
    try {
      const s = await api.saveOwnerSite(site);
      setSite(s.result || {});
      setMsg(t('settings.brandingSaved'));
    } catch {
      setMsg(t('settings.brandingSaveFailed'));
    } finally {
      setBusy(false);
    }
  };

  const claimSubdomain = async () => {
    setBusy(true);
    setMsg('');
    try {
      const r = await api.claimSubdomain(subInput.trim().toLowerCase());
      setSite(r.result);
      setSubdomainStatus(r.status);
      setMsg(t('settings.subdomainResult', { domain: r.fullDomain, status: r.status }));
    } catch (e: any) {
      setMsg(e?.response?.data?.message || t('settings.subdomainFailed'));
    } finally {
      setBusy(false);
    }
  };

  const addDomain = async () => {
    setBusy(true);
    setMsg('');
    try {
      const r = await api.addCustomDomain(domainInput.trim().toLowerCase());
      setSite(r.result);
      setCustomStatus(r.status);
      setMsg(t('settings.customDomainResult', { status: r.status }));
    } catch (e: any) {
      setMsg(e?.response?.data?.message || t('settings.customDomainFailed'));
    } finally {
      setBusy(false);
    }
  };

  const removeDomain = async () => {
    setBusy(true);
    try {
      await api.removeCustomDomain();
      setSite({ ...site, customDomain: undefined });
      setCustomStatus(undefined);
      setDomainLive(null);
      setMsg(t('settings.customDomainRemoved'));
    } finally {
      setBusy(false);
    }
  };

  const field = (label: string, key: keyof Tenant, type = 'text') => (
    <label className="block">
      <span className="text-sm opacity-80">{label}</span>
      <input
        type={type}
        value={(site[key] as string) || ''}
        onChange={(e) => setSite({ ...site, [key]: e.target.value })}
        className="mt-1 w-full px-3 py-2 border rounded-lg"
      />
    </label>
  );

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">
      <h1 className="text-2xl font-extrabold">{t('settings.title')}</h1>
      {msg && <div className="text-sm rounded-lg bg-blue-50 text-blue-800 px-3 py-2">{msg}</div>}

      {/* Branding */}
      <section className="surface rounded-xl p-5 space-y-4">
        <h2 className="font-bold">{t('settings.branding')}</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {field(t('settings.displayName'), 'displayName')}
          {field(t('settings.tagLine'), 'tagLine')}
          {field(t('settings.logoUrl'), 'logoUrl')}
          {field(t('settings.bannerImageUrl'), 'bannerImageUrl')}
          {field(t('settings.primaryColor'), 'primaryColor')}
          {field(t('settings.secondaryColor'), 'secondaryColor')}
        </div>
        <label className="block">
          <span className="text-sm opacity-80">{t('settings.about')}</span>
          <textarea
            value={site.aboutText || ''}
            onChange={(e) => setSite({ ...site, aboutText: e.target.value })}
            className="mt-1 w-full px-3 py-2 border rounded-lg"
            rows={3}
          />
        </label>
        <button onClick={saveBranding} className="btn-primary" disabled={busy}>
          {t('settings.saveBranding')}
        </button>
      </section>

      {/* Subdomain */}
      <section className="surface rounded-xl p-5 space-y-3">
        <h2 className="font-bold flex items-center gap-2">
          <Globe size={18} /> {t('settings.subdomain')}
        </h2>
        <p className="text-sm opacity-70" dangerouslySetInnerHTML={{ __html: t('settings.subdomainHint') }} />
        <div className="flex gap-2 items-center">
          <input
            value={subInput}
            onChange={(e) => setSubInput(e.target.value)}
            placeholder={t('settings.subdomainPlaceholder')}
            className="px-3 py-2 border rounded-lg flex-1"
          />
          <span className="opacity-70">.myezwheels.com</span>
          <button onClick={claimSubdomain} className="btn-outline" disabled={busy || !subInput}>
            {t('settings.reserve')}
          </button>
        </div>
        {subdomainStatus && <StatusPill status={subdomainStatus} />}
      </section>

      {/* Custom domain */}
      <section className="surface rounded-xl p-5 space-y-3">
        <h2 className="font-bold flex items-center gap-2">
          <Globe size={18} /> {t('settings.customDomain')}
        </h2>
        <p className="text-sm opacity-70" dangerouslySetInnerHTML={{ __html: t('settings.customDomainHint') }} />
        <div className="flex gap-2 items-center">
          <input
            value={domainInput}
            onChange={(e) => setDomainInput(e.target.value)}
            placeholder={t('settings.customDomainPlaceholder')}
            className="px-3 py-2 border rounded-lg flex-1"
          />
          <button onClick={addDomain} className="btn-outline" disabled={busy || !domainInput}>
            {t('settings.add')}
          </button>
          {site.customDomain && (
            <button onClick={removeDomain} className="text-sm text-red-600 hover:underline" disabled={busy}>
              {t('settings.remove')}
            </button>
          )}
        </div>
        {customStatus && <StatusPill status={customStatus} />}
        {domainLive && (
          <div className="text-xs opacity-70 border-t pt-2 mt-2 space-y-0.5">
            <div>{t('settings.dns', { state: domainLive.validationState })}</div>
            <div>{t('settings.certificate', { state: domainLive.certificateState })}</div>
            <div>{t('settings.deployment', { state: domainLive.deploymentStatus })}</div>
            {domainLive.isReady && (
              <div className="text-green-600 flex items-center gap-1">
                <Check size={13} /> {t('settings.liveSecured')}
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const { t } = useTranslation();
  const map: Record<string, { cls: string; icon: JSX.Element; label: string }> = {
    Active: { cls: 'bg-green-100 text-green-700', icon: <Check size={13} />, label: t('settings.statusActive') },
    Pending: { cls: 'bg-yellow-100 text-yellow-700', icon: <Loader2 size={13} className="animate-spin" />, label: t('settings.statusPending') },
    Failed: { cls: 'bg-red-100 text-red-700', icon: <AlertTriangle size={13} />, label: t('settings.statusFailed') },
  };
  const s = map[status] || { cls: 'bg-gray-100 opacity-80', icon: <></>, label: status };
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${s.cls}`}>
      {s.icon} {s.label}
    </span>
  );
}
